import React, { JSX } from 'react'
import { AnalyticsMetafield, ProductTotals } from 'types/api'
import { cleanVariables, graphqlRequest } from 'lib/helpers'
import { RefreshingBlock } from './Loading'
import getTopProducts from '../graphql/getTopProducts.graphql'
import { useQuery } from '@tanstack/react-query'
import { Leaderboard } from './Leaderboard'

interface ProductsLeaderboardProps {
  products?: AnalyticsMetafield[]
  bundles?: AnalyticsMetafield[]
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  token?: string
  client: AnalyticsMetafield | null
}

export const ProductsLeaderboard = ({ products, bundles, token, client, filters }: ProductsLeaderboardProps): JSX.Element => {
  const {
    data,
    isFetching
  } = useQuery({
    queryKey: ['dashboard', 'productsLeaderboards', filters, client?.id],

    queryFn: async () => {
      if (token == null) return
      let filteredBundles: AnalyticsMetafield[] = []
      let filteredProducts: AnalyticsMetafield[] = []

      if (filters.productOption?.length === 1 && filters.productOption[0].id === 'all') {
        filteredBundles = bundles ?? []
        filteredProducts = products ?? []
      } else {
        filteredBundles = filters?.productOption?.filter(p => (p.components?.length ?? 0) > 0) ?? []
        filteredProducts = filters?.productOption?.filter(p => (p.components?.length ?? 0) === 0) ?? []
      }

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: filteredProducts.map(p => p.id),
        bundleIds: filteredBundles.map(p => p.id),
        startDate: filters.range.startDate,
        endDate: filters.range.endDate,
        productView: 'BUNDLE'
      })

      const { productTotals } = await graphqlRequest<{ productTotals: ProductTotals[] }>(
        getTopProducts, cleanVar, token
      )

      return productTotals
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  return (
    <>
      <div className='relative shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden p-4 min-h-[280px] flex flex-col'>
        {isFetching && RefreshingBlock}
        <Leaderboard
          noDataMessage='No product data for specified filters'
          hoverData={false}
          idField='productId'
          data={data ?? []}
          defaultSortKey='quantity'
          columns={['productName', 'quantity']}
          title='Top products by units sold'
        />
      </div>

      <div data-testid='products-leaderboard-sales' className='relative shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden p-4 min-h-[280px] flex flex-col'>
        {isFetching && RefreshingBlock}
        <Leaderboard
          noDataMessage='No product data for specified filters'
          hoverData={false}
          idField='productId'
          data={data ?? []}
          defaultSortKey='sales'
          columns={['productName', 'sales']}
          title='Top products by sales'
        />
      </div>
    </>
  )
}
